import { useContext, useState } from "react";
import { OutsourcePanel, OutsourcePanelTitle } from "../../components";
import { AssigneeBox, DropdownMenuContent, DropdownMenuItem, DropdownMenuItemTitle, DropdownMenuItemUnassigned, DropdownMenuSeparator } from "./Styled";
import UserIcon from "../../../../shared/svg/UserIcon";
import { api } from "../../../../shared/hooks/api";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { error, success } from "../../../../utils/toast";
import { AuthenticationContext } from "../../../../provider/AuthenticationProvider";
import { OUTSOURCE_ORDER_STATUS, ROLE } from "../../../../data/enums";
import { color } from "../../../../shared/styles";

// Mapping of role keys to display names
const ROLE_DISPLAY: Record<string, string> = {
    logistic_staff: "Nhân viên hậu cần",
    first_storage_keeper: "Thủ kho 1",
    second_storage_keeper: "Thủ kho 2",
};

const normalizeAssignees = (value: any): any[] =>
    (Array.isArray(value) ? value : value ? [value] : [])
        .map((item: any) => item?.user || item)
        .filter(Boolean);

const AssigneeItems = ({ assignees }: { assignees: any[] }) => (
    <>
        {assignees.map((assignee: any, index: number) => (
            <span className="assignee-item" key={assignee?.id || assignee?.user_id || index}>
                <span className="assignee-icon"><UserIcon /></span>
                <span className="assignee-text">
                    <span className="assignee-name">
                        {assignee?.display_name || assignee?.username || assignee?.name || "Không rõ tên"}
                    </span>
                    {assignee?.email ? (
                        <span className="assignee-email">{assignee.email}</span>
                    ) : null}
                </span>
            </span>
        ))}
    </>
);

const Content = ({
    onChangeValue,
    assignees,
    title,
    role,
    locked = false,
    isUpdating = false,
}: {
    onChangeValue: Function;
    assignees: any[];
    title: string;
    role?: string;
    locked?: boolean;
    isUpdating?: boolean;
}) => {
    const [{ data, isLoading }] = api.get(
        `/users/?role=${role?.toUpperCase()}`
    );

    const {
        user: { role: currentRole },
    } = useContext(AuthenticationContext);

    const canEdit = (toEditRole?: string) => {
        switch (toEditRole) {
            case "logistic_staff":
                return [ROLE.MANAGER, ROLE.SALE_STAFF, ROLE.SALE_MANAGER].includes(currentRole);
             case "first_storage_keeper":
             case "second_storage_keeper":
                return [ROLE.MANAGER, ROLE.PRODUCTION_STAFF, ROLE.PURCHASING_STAFF].includes(
                    currentRole
                );
            default:
                return false;
        }
    };

    // Render read-only (locked) view without dropdown when locked
    if (locked) {
        return (
            <AssigneeBox>
                <div className="left">
                    <div className="title">{title}</div>
                    <div className="content locked" style={{ cursor: "default" }}>
                        {isUpdating ? (
                            "Đang cập nhật..."
                        ) : assignees.length > 0 ? (
                            <AssigneeItems assignees={assignees} />
                        ) : "Chưa bàn giao"}
                    </div>
                </div>
            </AssigneeBox>
        )
    }

    return (
        <AssigneeBox>
            <DropdownMenu.Root>
                <div className="left">
                    <div className="title">{title}</div>
                    <DropdownMenu.Trigger asChild>
                        <div className="content" style={{cursor: canEdit(role) ? "pointer" : "default"}}>
                            {assignees.length > 0
                                ? <AssigneeItems assignees={assignees} />
                                : "Chưa bàn giao"}
                        </div>
                    </DropdownMenu.Trigger>
                </div>

                <DropdownMenu.Portal>
                    <DropdownMenuContent
                        side="bottom"
                        align="start"
                        sideOffset={6}
                    >
                        <DropdownMenuItemTitle disabled>
                            {isLoading ? "Đang tải..." : `Chọn ${ROLE_DISPLAY[role || ""] || "người phụ trách"}`}
                        </DropdownMenuItemTitle>
                        <DropdownMenuSeparator />
                        <DropdownMenuItemUnassigned
                            onSelect={() => onChangeValue(undefined)}
                            style={{
                                padding: "8px 10px",
                                fontSize: 14,
                                cursor: "pointer",
                                borderRadius: 6,
                                fontFamily: "Noto Sans",
                                color: color.secondary.CHARCOAL,
                            }}
                        >
                            Bỏ chọn
                        </DropdownMenuItemUnassigned>
                        {(data?.results || []).map((user: any) => (
                            <DropdownMenuItem key={user.id} onSelect={() => onChangeValue(user)}>
                                <span>{user.display_name || user.username}</span>
                                {assignees.some((assignee: any) => assignee?.id === user.id) ? (
                                    <span style={{ color: color.primary.BLUE_B100 }}>✓</span>
                                ) : null}
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu.Portal>
            </DropdownMenu.Root>
        </AssigneeBox>
    );
};

interface AssigneeField {
    title: string;
    fieldName: string;
    role?: string;
}

export const Assignees = ({
    data: outsourceOrder,
    updateData,
    fetchData,
    mergeOutsourceOrder,
    fields,
}: {
    data: any;
    updateData: Function;
    fetchData?: any;
    mergeOutsourceOrder?: Function;
    fields: AssigneeField[];
    disabledEdit?: boolean;
}) => {
    const [updatingField, setUpdatingField] = useState<string | null>(null);

    const onChangeAssignee = (field: string, role?: string) => async (assignee: any) => {
        if (field === "first_storage_keeper" || field === "second_storage_keeper") return;
        if (updatingField) return;
        setUpdatingField(field);
        try {
            const current = outsourceOrder[field] || null;

            // If no assignee selected and no current assignee → no change
            if (!assignee) {
                if (!current) {
                    success("THÀNH CÔNG", `${ROLE_DISPLAY[role || ""] || "Nhân sự"} - không có thay đổi`);
                    return;
                }
            }
            
            // If the selected user is already assigned → no change
            if (assignee?.id && current && current["id"] === assignee.id) {
                success("THÀNH CÔNG", `${ROLE_DISPLAY[role || ""] || "Nhân sự"} - không có thay đổi`);
                return;
            }

            const updatePayload = assignee?.id
                ? { [field]: assignee?.id }
                : { [field]: "" };

            await updateData(updatePayload);

            // Optimistically update local state to avoid full refresh and flicker
            if (mergeOutsourceOrder) {
                const localUpdatePayload = assignee
                    ? { [field]: assignee }
                    : { [field]: null };
                mergeOutsourceOrder(localUpdatePayload);
            } else if (fetchData) {
                // Fallback to refectch if mergeMaterialOrder is not provided
                await fetchData();
            }

            const roleText = ROLE_DISPLAY[role || ""] || "Nhân sự";
            const actionText = assignee?.id ? "Bàn giao" : "Bỏ chọn";
            const nameText = assignee?.display_name || assignee?.username || "";

            success(
                "THÀNH CÔNG",
                `${actionText}${nameText ? ` - ${nameText}` : ""}${roleText ? ` (${roleText})` : ""} thành công`,
            );
        } catch (err) {
            const roleText = ROLE_DISPLAY[role || ""] || "Nhân sự";
            error(`${roleText} - cập nhật thất bại`);
        } finally {
            setUpdatingField(null);
        }
    };

    return (
        <OutsourcePanel>
            <OutsourcePanelTitle>Phụ trách</OutsourcePanelTitle>
            {fields.map((item) => {
                const currentStatus = String(outsourceOrder?.status);
                const isStorageKeeperField =
                    item.fieldName === "first_storage_keeper" ||
                    item.fieldName === "second_storage_keeper";
                const isLocked =
                    currentStatus !== OUTSOURCE_ORDER_STATUS.OPEN ||
                    isStorageKeeperField;

                return (
                    <Content
                        key={item.fieldName}
                        title={item.title}
                        onChangeValue={onChangeAssignee(item.fieldName, item.role)}
                        assignees={normalizeAssignees((outsourceOrder as any)[item.fieldName as any])}
                        role={item.role}
                        locked={isLocked || updatingField !== null}
                        isUpdating={updatingField === item.fieldName}
                    />
                );
            })}
        </OutsourcePanel>
    );
};
